<!-- @format -->
<template>
    <div v-cloak v-if="pagedItems.length > 0" class="row module transaction-history-module">
        <div class="header">
            <h2 class="centered-text">{{ title }}</h2>
        </div>
        <div class="row content">
            <ul class="table-head purple-gradient-bg">
                <li class="item" @click="sortByDate">
                    Date<span :class="dateSortClass" class="order-arrows"></span>
                </li>
                <li class="item" @click="sortByAmount">
                    Amount Paid<span :class="amountSortClass" class="order-arrows"></span>
                </li>
            </ul>
            <ul class="table-body">
                <li v-for="(item, index) in pagedItems" :key="index" class="table-row">
                    <ul class="table-row-columns">
                        <li class="item date">{{ item.date | date }}</li>
                        <li class="item amount">{{ item.amount | currency }}</li>
                    </ul>
                </li>
            </ul>
            <div v-show="items.length > 2" class="paging-row">
                <div class="entries">
                    Showing <span>{{ firstItemIndex }}</span> to <span>{{ lastItemIndex }}</span> of
                    <span>{{ items.length }}</span> entries
                </div>
                <div class="paging">
                    <button v-show="hasPreviousPage" class="previous-btn" @click="goToPreviousPage">
                        Prev
                    </button>
                    <ul>
                        <li v-for="(page, index) in pages" :key="index">
                            <router-link
                                to="#"
                                :class="{ 'is-active': page.isActive }"
                                @click.prevent="goToPageNumber(page)"
                            >
                                {{ page.pageNumber }}
                            </router-link>
                        </li>
                    </ul>
                    <button v-show="hasNextPage" class="next-btn" @click="goToNextPage">
                        Next
                    </button>
                </div>
            </div>
        </div>
        <loading-screen-svg></loading-screen-svg>
    </div>
</template>

<script>
import LoadingScreenSvg from '@/components/svg/loading-screen-svg';

export default {
    components: {
        'loading-screen-svg': LoadingScreenSvg,
    },

    props: {
        title: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            default: '',
        },
        restUrl: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            isInitializing: true,
            itemsPerPage: 6,
            pageNumber: 1,
            items: [],
            sortProperty: '',
            sortDirection: '',
            search: '',
        };
    },

    computed: {
        dateSortClass: function () {
            if (this.sortProperty == 'date') {
                return this.sortDirection;
            } else {
                return '';
            }
        },

        amountSortClass: function () {
            if (this.sortProperty == 'amount') {
                return this.sortDirection;
            } else {
                return '';
            }
        },
        viewableItems: function () {
            this.pageNumber = 1;

            var search = this.search;
            if (search) {
                return this.items.filter(function (item) {
                    return (
                        item.name.indexOf(search) !== -1 ||
                        item.website.indexOf(search) !== -1 ||
                        item.phone.indexOf(search) !== -1
                    );
                });
            }

            return this.items;
        },

        totalPages: function () {
            return Math.ceil(this.viewableItems.length / this.itemsPerPage);
        },

        pagedItems: function () {
            var firstItemIndex = (this.pageNumber - 1) * this.itemsPerPage;
            var lastItemIndex = firstItemIndex + parseInt(this.itemsPerPage);
            var itemsPage = this.viewableItems.slice(firstItemIndex, lastItemIndex);

            return itemsPage;
        },

        firstItemIndex: function () {
            return (this.pageNumber - 1) * this.itemsPerPage + 1;
        },

        lastItemIndex: function () {
            var lastItemIndex = this.pageNumber * this.itemsPerPage;
            var totalCount = this.viewableItems.length;

            if (lastItemIndex > totalCount) {
                return totalCount;
            } else {
                return lastItemIndex;
            }
        },

        hasPreviousPage: function () {
            return this.pageNumber !== 1;
        },

        hasNextPage: function () {
            return this.pageNumber !== this.totalPages;
        },

        pages: function () {
            var maxPages = 5;
            var currentPage = this.pageNumber;
            var totalPages = this.totalPages;

            var pages = [];
            var startIndex = 0;
            var endIndex = 0;

            if (totalPages <= maxPages) {
                startIndex = 0;
                endIndex = totalPages;
            } else if (currentPage <= maxPages - 2) {
                startIndex = 0;
                endIndex = maxPages;
            } else if (currentPage <= totalPages - 2) {
                startIndex = currentPage - 3;
                endIndex = currentPage + 2;
            } else {
                startIndex = totalPages - 5;
                endIndex = totalPages;
            }

            for (var i = startIndex; i < endIndex; i++) {
                pages.push({
                    pageNumber: i + 1,
                    isActive: currentPage == i + 1,
                });
            }

            return pages;
        },
    },

    created() {
        var self = this;

        self.$api
            .get(self.restUrl)
            .then((response) => {
                var transactions = [];

                for (var i = 0; i < response.data.data.length; i++) {
                    var transaction = {
                        date: new Date(response.data.data[i].PaymentDate),
                        amount: response.data.data[i].Amount || 0,
                    };

                    transactions.push(transaction);
                }

                self.items = transactions;
                self.sortByDate();
                self.isInitializing = false;
            })
            .catch((error) => {
                this.$helpers.showFailNotification(
                    'An error occurred while fetching your information.',
                    { error },
                );
            });
    },

    methods: {
        goToNextPage: function () {
            if (this.pageNumber < this.totalPages) {
                this.pageNumber = this.pageNumber + 1;
            }
        },

        goToPreviousPage: function () {
            if (this.pageNumber != 1) {
                this.pageNumber = this.pageNumber - 1;
            }
        },

        goToPageNumber: function (page) {
            this.pageNumber = page.pageNumber;
        },

        sortByDate: function () {
            var ascSortFunction = function (a, b) {
                return a.date - b.date;
            };

            var descSortFunction = function (a, b) {
                return b.date - a.date;
            };

            this.sortByProperty('date', ascSortFunction, descSortFunction);
        },

        sortByAmount: function () {
            var ascSortFunction = function (a, b) {
                return a.amount - b.amount;
            };

            var descSortFunction = function (a, b) {
                return b.amount - a.amount;
            };

            this.sortByProperty('amount', ascSortFunction, descSortFunction);
        },

        sortByProperty: function (sortProperty, ascSortFunction, descSortFunction) {
            if (this.sortProperty != sortProperty) {
                this.sortProperty = sortProperty;
                this.sortDirection = 'desc';
                this.items.sort(descSortFunction);
            } else if (this.sortDirection == 'asc') {
                this.sortDirection = 'desc';
                this.items.sort(descSortFunction);
            } else {
                this.sortDirection = 'asc';
                this.items.sort(ascSortFunction);
            }
        },
    },
};
</script>

<style>
.transaction-history-module .header {
    padding: 20px 0;
}

.transaction-history-module .content {
    padding: 0 0 20px;
}

.transaction-history-module h2 {
    color: var(--color-text-default: );
}

.transaction-history-module .item {
    position: relative;
    display: inline-block;
    *display: inline;
    zoom: 1;
    width: 49.2%;
    vertical-align: middle;
}

.transaction-history-module .table-head .item {
    padding: 20px;
}

.transaction-history-module .table-head .item:hover {
    cursor: pointer;
}

.transaction-history-module .table-body .item {
    padding: 15px 25px;
}

.transaction-history-module .table-body .date {
    color: var(--color-text-default);
}

.transaction-history-module .table-body .amount {
    text-align: center;
    font-weight: 600;
    color: var(--color-text-accent-limegreen);
}

.transaction-history-module .order-arrows {
    top: calc(50% - 15px);
}

.transaction-history-module .paging-row {
    border-radius: 0;
    -webkit-box-shadow: 0px 5px 5px 0px rgba(214, 214, 214, 1);
    -moz-box-shadow: 0px 5px 5px 0px rgba(214, 214, 214, 1);
    box-shadow: 0px -5px 5px 0px rgba(214, 214, 214, 1);
}
</style>
